import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Paper,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link } from "react-router-dom";

import { USER_ROLES } from "../../utils/constants";
import CustomButton from "../UI/CustomButton";
import SaveForFollowingVisitsCheckbox, {
  canSaveForFollowingVisits,
} from "./SaveForFollowingVisitsCheckbox";
import useUpdateVisitPhotosMutation from "../../hooks/useUpdateVisitPhotosMutation";
import useGetVisitPhotos from "../../hooks/useGetVisitPhotos";

const PhotosModule = ({
  role,
  selectedSessionId,
  selectedVisit,
  patientId,
  visitId,
  step,
  photoTypes = [],
  disabled = false,
  showSelectAll = true,
  headerless = false,
  manageTestId,
  isTrialLocked = false,
}) => {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(false);

  // Local dialog state — tracks pending changes before Save is clicked
  const [localSelectedPhotos, setLocalSelectedPhotos] = useState([]);
  const [localIsAllPhotos, setLocalIsAllPhotos] = useState(false);
  const [localDisallowInv, setLocalDisallowInv] = useState(false);
  const [localApplyToFollowing, setLocalApplyToFollowing] = useState(false);

  const stepId = step?.id;
  const label = step?.title || "Photos";
  const testIdSuffix = stepId ? `-${stepId}` : "";

  const updateMutation = useUpdateVisitPhotosMutation();

  // Step-scoped truth from the backend — always fetched (not just in patient mode) so a
  // repeated module's CRO view reads this step's own items instead of the flat, union-of-
  // all-steps `selectedVisit.extra_photos` field.
  const { data: visitPhotosData } = useGetVisitPhotos({
    visitId,
    patientId,
    sessionId: selectedSessionId,
    stepId,
  });

  const isInvestigator =
    role === USER_ROLES.INVESTIGATOR ||
    role === USER_ROLES.SUB_INVESTIGATOR ||
    role === USER_ROLES.CRC;

  const isReadOnly =
    isTrialLocked ||
    role === USER_ROLES.REVIEWER ||
    (patientId && selectedVisit.visit_number === -1) ||
    (patientId && selectedVisit?.disallow_inv_photos_selection);

  // Adverse Event visits must always keep the investigator locked out of picking photos —
  // CRO can't turn this off.
  const isAdverseEventVisit = selectedVisit?.visit_number === -1;

  // Sync enabled state from server data. In patient mode, only an explicit session
  // selection counts as "on" — items merely inherited from the visit are not a real choice yet.
  // Exception: when the investigator is locked out of picking (disallow_inv), there will
  // never be an explicit session selection, so the visit's forced items count as "on" instead.
  useEffect(() => {
    if (!patientId) {
      if (visitPhotosData?.extra_photos?.length > 0) {
        setEnabled(true);
      }
    } else if (
      (visitPhotosData?.is_session_selection || visitPhotosData?.disallow_inv_photos_selection) &&
      visitPhotosData?.extra_photos?.length > 0
    ) {
      setEnabled(true);
    }
  }, [patientId, visitPhotosData]);

  // Initialise local dialog flags from this step's visit-level truth each time the dialog opens
  useEffect(() => {
    if (open) {
      setLocalIsAllPhotos(!!visitPhotosData?.is_all_photos);
      setLocalDisallowInv(isAdverseEventVisit || !!visitPhotosData?.disallow_inv_photos_selection);
      setLocalApplyToFollowing(!!selectedVisit?.apply_photos_to_following);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-populate the investigator's selection — only when the session has an explicit
  // override; otherwise the visit's items are just inherited, not actually chosen yet
  useEffect(() => {
    if (open) {
      if (!patientId) {
        setLocalSelectedPhotos(visitPhotosData?.extra_photos || []);
      } else {
        setLocalSelectedPhotos(
          visitPhotosData?.is_session_selection || visitPhotosData?.disallow_inv_photos_selection
            ? visitPhotosData?.extra_photos || []
            : []
        );
      }
    }
  }, [open, patientId, visitPhotosData]); // eslint-disable-line react-hooks/exhaustive-deps

  const localSelectedIds = useMemo(
    () => localSelectedPhotos.map((p) => p.id),
    [localSelectedPhotos]
  );

  const allSelected = photoTypes.length > 0 && localSelectedIds.length === photoTypes.length;

  const handleToggleEnabled = (e) => {
    const checked = e.target.checked;
    setEnabled(checked);

    if (!checked) {
      updateMutation.mutate({
        visitId,
        patientId,
        sessionId: selectedSessionId,
        stepId,
        extraPhotos: [],
        isAllPhotos: false,
        disallowInvPhotosSelection: false,
        applyPhotosToFollowing: false,
      });
    }
  };

  const togglePhoto = (photo) => {
    if (isReadOnly) return;

    setLocalSelectedPhotos((prev) => {
      const exists = prev.some((p) => p.id === photo.id);
      return exists ? prev.filter((p) => p.id !== photo.id) : [...prev, photo];
    });
  };

  const handleSelectAll = () => {
    if (isReadOnly) return;
    setLocalSelectedPhotos(allSelected ? [] : photoTypes);
  };

  const handleSaveForAllVisits = () => {
    setLocalIsAllPhotos((prev) => !prev);
  };

  const handleDisableInvSelection = () => {
    setLocalDisallowInv((prev) => !prev);
  };

  const handleSave = () => {
    updateMutation.mutate(
      {
        visitId,
        patientId,
        sessionId: selectedSessionId,
        stepId,
        extraPhotos: localSelectedPhotos.map((p) => ({ label: p.label || p.name_str })),
        isAllPhotos: localIsAllPhotos,
        disallowInvPhotosSelection: headerless ? false : localDisallowInv,
        applyPhotosToFollowing: localApplyToFollowing,
      },
      { onSuccess: () => setOpen(false) }
    );
  };

  // Investigators are not shown modules the CRO did not configure for this visit
  if (
    isInvestigator &&
    patientId &&
    !headerless &&
    selectedVisit?.visit_number >= 0 &&
    visitPhotosData?.visit_configured === false
  ) {
    return null;
  }

  return (
    <Box sx={headerless ? {} : { width: "100%" }}>
      {headerless ? (
        <CustomButton
          size="small"
          variant="outlined"
          onClick={() => setOpen(true)}
          data-testid={manageTestId}
        >
          {isReadOnly ? "View" : "Manage"}
        </CustomButton>
      ) : (
        /* Header */
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Checkbox
              checked={enabled}
              onChange={handleToggleEnabled}
              disabled={isReadOnly || disabled || updateMutation.isPending}
              data-testid={`photos-enable-checkbox${testIdSuffix}`}
              inputProps={{ "data-testid": `photos-enable-checkbox-input${testIdSuffix}` }}
            />
            <Typography variant="subtitle1" fontWeight={600}>
              {label}
            </Typography>
          </Stack>
          {enabled && (
            <Stack direction="row" spacing={1} alignItems="center">
              {patientId && (
                <Tooltip title="Go to Photos">
                  <IconButton
                    component={Link}
                    target="_blank"
                    to={`/patient/${patientId}/photos/${!!selectedSessionId ? selectedSessionId : selectedVisit.photo_session_id}/?visitName=${encodeURIComponent(selectedVisit.visit_name)}`}
                    size="small"
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <CustomButton
                size="small"
                variant="outlined"
                data-testid={`photos-manage-btn${testIdSuffix}`}
                onClick={() => setOpen(true)}
              >
                {isReadOnly ? "View" : "Manage"}
              </CustomButton>
            </Stack>
          )}
        </Stack>
      )}

      {/* Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isReadOnly ? "View" : "Manage"} {label}
        </DialogTitle>

        <DialogContent dividers>
          {/* CRO flags */}
          {!isReadOnly &&
            (role === USER_ROLES.CRO ||
              role === USER_ROLES.CRO_COORDINATOR ||
              role === USER_ROLES.DATA_MANAGER ||
              role === USER_ROLES.SUPER_ADMIN) && (
              <Box sx={{ mb: 1 }}>
                {!headerless && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Checkbox
                      checked={localDisallowInv}
                      onChange={handleDisableInvSelection}
                      disabled={isAdverseEventVisit}
                    />
                    <Typography variant="body2">
                      Disable photos selection for investigator
                      {isAdverseEventVisit && " (always enabled for Adverse Event visits)"}
                    </Typography>
                  </Stack>
                )}

                {!patientId && canSaveForFollowingVisits(selectedVisit) && (
                  <SaveForFollowingVisitsCheckbox
                    label="Save photos configurations for all coming visits"
                    checked={localIsAllPhotos}
                    onChange={handleSaveForAllVisits}
                  />
                )}
              </Box>
            )}

          {/* Select all */}
          {showSelectAll && !isReadOnly && (
            <Typography
              variant="body2"
              sx={{ cursor: "pointer", color: "primary.main", mb: 1 }}
              onClick={handleSelectAll}
            >
              {allSelected ? "Deselect All Photos: " : "Select All Photos: "}
              {localSelectedIds.length}
            </Typography>
          )}

          {/* Max info */}
          {isInvestigator && selectedVisit?.is_maximum_photo_types && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Maximum Photos: {selectedVisit?.maximum_photo_types}
            </Typography>
          )}

          {/* List */}
          <List>
            {photoTypes.map((photo) => {
              const checked = localSelectedIds.includes(photo.id);

              return (
                <ListItem
                  key={photo.id}
                  disableGutters
                  sx={{ mb: 1 }}
                  data-photo-label={photo.label}
                >
                  <Paper
                    sx={{
                      p: 1.5,
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Checkbox
                      checked={checked}
                      disabled={isReadOnly}
                      onChange={() => togglePhoto(photo)}
                    />
                    <ListItemText primary={photo.label} />
                  </Paper>
                </ListItem>
              );
            })}
          </List>

          {/* Apply to following visits - Investigator/CRC */}
          {!isReadOnly && isInvestigator && patientId && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
              <Checkbox
                checked={localApplyToFollowing}
                onChange={() => setLocalApplyToFollowing((prev) => !prev)}
              />
              <Typography variant="body2">Apply to all following visits</Typography>
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          {!isReadOnly && (
            <Button variant="contained" onClick={handleSave} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? "Saving..." : "Save"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PhotosModule;
